/**
 * ACTIVE-BRANCH BINDING (versioning plan item 1a / Phase B; masterplan 67
 * §5b COW lens).
 *
 * Under the COW lens a branch checkout is NOT navigation: the client keeps
 * the SAME project id and names the branch on every request — the backend
 * middleware binds `?branch=` / `x-branch` to the request scope and the
 * lens serves that branch's twins. This module is the client half:
 *
 *   - ONE binding `{ islandId, branch }` for the session (`main` = none).
 *   - `branchHeaders()` — every fetch wrapper spreads it into its headers.
 *   - `?branch=` in the URL is the shareable, sticky form: the project
 *     layout syncs URL → binding on load and binding → URL on switch.
 *   - sessionStorage keeps the binding across reloads of the same island.
 *
 * KNOWN LIMIT (Phase C closes it): the header rides every request, so a
 * request for ANOTHER island while a project branch is bound also carries
 * the branch name; the ws connection and its subscriptions carry no branch
 * yet (patches are branch-undifferentiated until branch-tagged
 * subscriptions land).
 */

import { useSyncExternalStore } from "react";

export type ActiveBranch = { islandId: string; branch: string } | null;

const STORAGE_KEY = "sp.activeBranch";
const MAIN = "main";

let current: ActiveBranch = null;
let loaded = false;
const listeners = new Set<() => void>();

function load(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v.islandId === "string" && typeof v.branch === "string" && v.branch !== MAIN) {
        current = { islandId: v.islandId, branch: v.branch };
      }
    }
  } catch {
    /* storage unavailable — in-memory only */
  }
}

function persist(): void {
  try {
    if (typeof window === "undefined") return;
    if (current) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function emit(): void {
  listeners.forEach((l) => l());
}

/** The current binding (null = main everywhere). */
export function getActiveBranch(): ActiveBranch {
  load();
  return current;
}

/** The branch bound for `islandId` — `main` when unbound or bound elsewhere. */
export function activeBranchFor(islandId: string | undefined | null): string {
  const b = getActiveBranch();
  return b && islandId && b.islandId === islandId ? b.branch : MAIN;
}

/** Bind `branch` for `islandId`; `main` (or empty) clears the binding. */
export function setActiveBranch(islandId: string, branch: string | null | undefined): void {
  load();
  const next: ActiveBranch =
    branch && branch !== MAIN ? { islandId, branch } : null;
  if (
    (next === null && current === null) ||
    (next && current && next.islandId === current.islandId && next.branch === current.branch)
  )
    return;
  current = next;
  persist();
  emit();
}

export function clearActiveBranch(): void {
  setActiveBranch("", null);
}

/** Headers every backend request carries — `x-branch` when bound. (A READ
 *  also names its holder: `readHolderHeaders`, active-holder.ts.) */
export function branchHeaders(): Record<string, string> {
  const b = getActiveBranch();
  return b ? { "x-branch": b.branch } : {};
}

/** Append `?branch=` for the bound branch (URLs the header cannot reach). */
export function withBranchParam(url: string): string {
  const b = getActiveBranch();
  if (!b) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}branch=${encodeURIComponent(b.branch)}`;
}

export function subscribeActiveBranch(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** React binding — re-renders on switch. */
export function useActiveBranch(): ActiveBranch {
  return useSyncExternalStore(subscribeActiveBranch, getActiveBranch, () => null);
}

/** The branch this island is on, reactive. */
export function useIslandBranch(islandId: string | undefined | null): string {
  const b = useActiveBranch();
  return b && islandId && b.islandId === islandId ? b.branch : MAIN;
}
